import { useEffect, useState } from "react";
import { CalendarDays, CheckCircle2, Images, MapPin, UserPlus } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { api, formatApiErrorDetail, imgUrl } from "@/lib/api";
import { useLang } from "@/i18n";

const EMPTY_FORM = { name: "", email: "", phone: "", company: "", participants: 1 };

const daysUntil = (d) => {
  if (!d) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const start = new Date(`${d}T00:00:00`);
  if (isNaN(start.getTime())) return null;
  return Math.round((start - today) / 86400000);
};

export default function Activities() {
  const [items, setItems] = useState([]);
  const [registerFor, setRegisterFor] = useState(null);
  const [galleryFor, setGalleryFor] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [sending, setSending] = useState(false);
  const [signupDone, setSignupDone] = useState(null);
  const { t } = useLang();

  useEffect(() => {
    api.get("/activities").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  if (items.length === 0) return null;

  const openRegister = (a) => {
    setRegisterFor(a);
    setSignupDone(null);
    setForm(EMPTY_FORM);
  };

  const openGallery = (a) => {
    setGalleryFor(a);
    setActiveImg(0);
  };

  const submitSignup = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await api.post("/training-signups", {
        activity_id: registerFor.id,
        ...form,
        participants: Number(form.participants) || 1,
      });
      setSignupDone(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("signup_failed"));
    } finally {
      setSending(false);
    }
  };

  const field =
    "bg-[#0B1120] border-white/10 text-slate-100 placeholder:text-slate-600 focus-visible:ring-[#ED1C24] rounded-none h-12";

  const galleryImages = galleryFor
    ? [{ url: galleryFor.image, caption: "" }, ...(galleryFor.gallery || [])]
        .map((g) => (typeof g === "string" ? { url: g, caption: "" } : g))
        .filter((g) => g.url)
    : [];

  return (
    <section
      id="activities"
      data-testid="activities-section"
      className="bg-[#0F172A] px-6 md:px-12 lg:px-24 py-20 lg:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <Reveal>
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-4">
            {t("activities_label")}
          </p>
          <h2 className="font-display font-semibold text-white text-4xl sm:text-5xl tracking-tight">
            {t("activities_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-slate-400 max-w-sm text-sm lg:text-base">{t("activities_sub")}</p>
        </Reveal>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {items.map((a, i) => {
          const galleryCount = (a.gallery || []).length;
          const latest = galleryCount > 0 ? a.gallery[galleryCount - 1] : null;
          const cover = latest ? (typeof latest === "string" ? latest : latest.url) : a.image;
          const coverCaption = latest && typeof latest === "object" ? latest.caption : "";
          const startsIn = daysUntil(a.starts_at);
          return (
            <Reveal key={a.id} delay={(i % 3) * 0.08}>
              <article
                data-testid={`activity-card-${i}`}
                className="group h-full bg-[#0B1120] border border-white/10 hover:border-[#ED1C24]/60 hover:-translate-y-2 transition-[border-color,transform] duration-500 flex flex-col"
              >
                {cover && (
                  <div
                    className={`relative h-56 overflow-hidden ${galleryCount > 0 ? "cursor-pointer" : ""}`}
                    onClick={() => galleryCount > 0 && openGallery(a)}
                  >
                    <img
                      src={imgUrl(cover)}
                      alt={a.title}
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    />
                    <span className="absolute top-3 left-3 bg-[#ED1C24] text-white text-[10px] tracking-[0.15em] uppercase px-3 py-1">
                      {a.kind}
                    </span>
                    {galleryCount > 0 && (
                      <span data-testid={`activity-latest-tag-${i}`} className="absolute top-3 right-3 bg-[#0B1120]/85 backdrop-blur border border-[#FFC61A]/50 text-[#FFC61A] text-[10px] tracking-[0.15em] uppercase px-3 py-1">
                        {t("activity_latest")}
                      </span>
                    )}
                    {coverCaption && (
                      <span data-testid={`activity-cover-caption-${i}`} className="absolute bottom-3 left-3 right-24 bg-[#0B1120]/85 backdrop-blur text-slate-200 text-xs px-3 py-1.5 leading-snug">
                        {coverCaption}
                      </span>
                    )}
                    {galleryCount > 0 && (
                      <button
                        data-testid={`activity-gallery-btn-${i}`}
                        onClick={(e) => { e.stopPropagation(); openGallery(a); }}
                        className="absolute bottom-3 right-3 inline-flex items-center gap-2 bg-[#0B1120]/85 backdrop-blur border border-white/20 text-white text-[11px] px-3 py-1.5 hover:border-[#FFC61A] transition-colors"
                      >
                        <Images size={13} /> {t("view_gallery")} ({galleryCount + 1})
                      </button>
                    )}
                  </div>
                )}
                <div className="p-6 flex flex-col flex-1">
                  {!cover && (
                    <span className="self-start bg-[#ED1C24]/15 border border-[#ED1C24]/30 text-[#ED1C24] text-[10px] tracking-[0.15em] uppercase px-3 py-1 mb-4">
                      {a.kind}
                    </span>
                  )}
                  <h3 className="font-display font-semibold text-white text-xl leading-snug mb-3">
                    {a.title}
                  </h3>
                  {a.description && (
                    <p className="text-sm text-slate-400 leading-relaxed flex-1">{a.description}</p>
                  )}
                  <div className="flex flex-wrap items-center justify-between gap-3 mt-5 pt-4 border-t border-white/5">
                    <div className="flex flex-wrap gap-4 text-xs text-slate-500">
                      {startsIn !== null && startsIn >= 0 && (
                        <span
                          data-testid={`activity-countdown-${i}`}
                          className={`inline-flex items-center gap-1.5 font-medium uppercase tracking-wide px-2 py-1 border ${
                            startsIn <= 3
                              ? "text-amber-400 border-amber-400/40 bg-amber-400/10"
                              : "text-emerald-400 border-emerald-400/40 bg-emerald-400/10"
                          }`}
                        >
                          {startsIn === 0 ? t("act_starts_today") : t("act_starts_in").replace("{n}", startsIn)}
                        </span>
                      )}
                      {a.date && (
                        <span className="inline-flex items-center gap-1.5">
                          <CalendarDays size={13} className="text-[#FFC61A]" /> {a.date}
                        </span>
                      )}
                      {a.location && (
                        <span className="inline-flex items-center gap-1.5">
                          <MapPin size={13} className="text-[#FFC61A]" /> {a.location}
                        </span>
                      )}
                    </div>
                    {a.kind === "Training" && (
                      startsIn !== null && startsIn < 0 ? (
                        <span
                          data-testid={`activity-closed-${i}`}
                          className="inline-flex items-center gap-2 border border-white/15 text-slate-500 text-xs font-medium px-4 py-2 uppercase tracking-wide"
                        >
                          {t("act_closed")}
                        </span>
                      ) : (
                        <button
                          data-testid={`activity-register-btn-${i}`}
                          onClick={() => openRegister(a)}
                          className="inline-flex items-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-xs font-medium px-4 py-2 transition-colors duration-300"
                        >
                          <UserPlus size={13} /> {t("register_btn")}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          );
        })}
      </div>

      <Dialog open={!!registerFor} onOpenChange={(v) => !v && setRegisterFor(null)}>
        <DialogContent data-testid="signup-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{t("signup_title")}</DialogTitle>
          </DialogHeader>
          {signupDone ? (
            <div className="text-center py-6">
              <CheckCircle2 size={48} className="text-[#FFC61A] mx-auto mb-5" />
              <p className="font-display text-2xl text-white mb-2">
                {t("signup_success").replace(".", "")}<span className="text-[#ED1C24]">.</span>
              </p>
              <p className="text-xs text-slate-500 mb-1">{t("signup_ref")}</p>
              <p data-testid="signup-number" className="font-display text-xl text-[#FFC61A] mb-5">
                {signupDone.signup_number}
              </p>
              <p className="text-sm text-slate-400 mb-8">{t("signup_note")}</p>
              <button
                data-testid="signup-again-btn"
                onClick={() => { setSignupDone(null); setForm(EMPTY_FORM); }}
                className="border border-white/15 hover:border-[#ED1C24] text-slate-300 text-sm px-6 py-2.5 transition-colors duration-300"
              >
                {t("signup_again")}
              </button>
            </div>
          ) : (
            <form data-testid="signup-form" onSubmit={submitSignup} className="space-y-4">
              <p className="text-sm text-[#FFC61A] border-l-2 border-[#ED1C24] pl-3">{registerFor?.title}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Input data-testid="signup-name-input" required placeholder={t("ph_name")} value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} className={field} />
                <Input data-testid="signup-email-input" required type="email" placeholder={t("ph_email")} value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })} className={field} />
                <Input data-testid="signup-phone-input" placeholder={t("ph_phone")} value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })} className={field} />
                <Input data-testid="signup-company-input" placeholder={t("ph_company")} value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })} className={field} />
              </div>
              <Input data-testid="signup-participants-input" type="number" min="1" max="100"
                placeholder={t("signup_participants")} value={form.participants}
                onChange={(e) => setForm({ ...form, participants: e.target.value })} className={field} />
              <button
                data-testid="signup-submit-btn"
                type="submit"
                disabled={sending}
                className="w-full bg-[#ED1C24] hover:bg-[#C81016] disabled:opacity-60 text-white font-medium py-3.5 transition-colors duration-300"
              >
                {sending ? t("processing") : t("signup_submit")}
              </button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!galleryFor} onOpenChange={(v) => !v && setGalleryFor(null)}>
        <DialogContent data-testid="gallery-dialog" className="bg-[#0F172A] text-slate-100 border-white/10 rounded-none sm:max-w-4xl">
          <DialogHeader>
            <DialogTitle className="font-display text-xl pr-8">{galleryFor?.title}</DialogTitle>
          </DialogHeader>
          {galleryImages.length > 0 && (
            <div>
              <div className="border border-white/10 overflow-hidden mb-2">
                <img
                  data-testid="gallery-main-image"
                  src={imgUrl(galleryImages[activeImg].url)}
                  alt={galleryFor?.title}
                  className="w-full h-[420px] object-cover"
                />
              </div>
              {galleryImages[activeImg].caption && (
                <p data-testid="gallery-caption" className="text-sm text-slate-400 mb-3 leading-relaxed">
                  {galleryImages[activeImg].caption}
                </p>
              )}
              <div className="flex gap-3 overflow-x-auto pb-1 mt-2">
                {galleryImages.map((g, i) => (
                  <button
                    key={i}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setActiveImg(i)}
                    className={`shrink-0 w-20 h-16 overflow-hidden border transition-colors duration-300 ${
                      i === activeImg ? "border-[#ED1C24]" : "border-white/10 hover:border-white/40"
                    }`}
                  >
                    <img src={imgUrl(g.url)} alt={g.caption || ""} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
